import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Calendar as CalendarIcon, Loader2, MapPin, Cloud, Sun, CloudRain, CloudSnow, CloudFog, CloudDrizzle, CloudLightning, Fuel, ReceiptText, Route, Phone, AlertTriangle, FilterX } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const ICONS = {
  "sun": Sun, "cloud": Cloud, "cloud-sun": Cloud, "cloud-rain": CloudRain,
  "cloud-snow": CloudSnow, "cloud-fog": CloudFog, "cloud-drizzle": CloudDrizzle,
  "cloud-lightning": CloudLightning,
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short", year: "numeric" }); } catch { return iso; }
}
function fmtTime(iso) {
  if (!iso) return ""; try { return new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
}

function MeteoBadge({ meteo }) {
  if (!meteo) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs" style={{ background: "var(--surface-muted)", color: "var(--text-muted)" }}>
        <AlertTriangle className="w-3 h-3" /> Meteo non disponibile
      </span>
    );
  }
  const Icon = ICONS[meteo.icon] || Cloud;
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: "#E0F2FE", color: "#075985" }}>
      <Icon className="w-3.5 h-3.5" />
      {meteo.temp_min}°/{meteo.temp_max}° · {meteo.label}
      {meteo.precip_prob != null && meteo.precip_prob > 0 && <span>· 💧{meteo.precip_prob}%</span>}
    </span>
  );
}

export default function PianificazioneInterventi() {
  const [data, setData] = useState({ items: [], citta_base: "", totale_costi: 0, totale_km: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [squadre, setSquadre] = useState([]);
  const [squadraId, setSquadraId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = async () => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams();
      if (squadraId) params.set("squadra_id", squadraId);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const { data } = await api.get(`/pianificazione-interventi?${params}`);
      setData(data);
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      if (!msg.includes("Città base")) toast.error(msg);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try { const s = await api.get("/squadre"); setSquadre(s.data); } catch {}
      await load();
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [squadraId, dateFrom, dateTo]);

  const hasFilters = !!(squadraId || dateFrom || dateTo);
  const clearFilters = () => { setSquadraId(""); setDateFrom(""); setDateTo(""); };

  return (
    <div data-testid="pian-interventi-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: "var(--primary)" }}>
            <Route className="w-3.5 h-3.5" /> Logistica trasferte
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>Pianificazione interventi</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }} data-testid="pi-subtitle">
            {data.citta_base ? <>Partenza: <strong>{data.citta_base}</strong> · ordine dal più lontano al più vicino</> : "Imposta la città base in Impostazioni → Trasferta"}
          </p>
        </div>
      </div>

      {error && error.includes("Città base") && (
        <div className="card-shell p-6 mb-4" style={{ background: "#FFF8E1", borderLeft: "4px solid #F59E0B" }} data-testid="pi-error-base">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 mt-0.5" style={{ color: "#B45309" }} />
            <div>
              <div className="font-semibold mb-1">Città base non configurata</div>
              <p className="text-sm mb-2" style={{ color: "var(--text-muted)" }}>
                Per calcolare distanze, costi trasferta e meteo serve impostare la città di partenza.
              </p>
              <Link to="/impostazioni" className="text-sm font-semibold hover:underline" style={{ color: "var(--primary)" }}>
                Vai a Impostazioni →
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* KPI summary */}
      {!loading && data.items.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <KPI icon={CalendarIcon} label="Interventi" value={data.total} testid="pi-kpi-count" />
          <KPI icon={Route} label="Km totali (a/r)" value={`${data.totale_km.toLocaleString("it-IT")} km`} testid="pi-kpi-km" />
          <KPI icon={ReceiptText} label="Costo trasferte tot." value={`€ ${data.totale_costi.toLocaleString("it-IT", { minimumFractionDigits: 2 })}`} testid="pi-kpi-costi" />
        </div>
      )}

      <div className="card-shell p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <select value={squadraId} onChange={(e) => setSquadraId(e.target.value)}
            className="px-3 py-2 text-sm rounded-md border outline-none" style={{ borderColor: "var(--border)", background: "#fff" }}
            data-testid="pi-filter-squadra">
            <option value="">Tutte le squadre</option>
            {squadre.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="px-3 py-2 text-sm rounded-md border outline-none" style={{ borderColor: "var(--border)", background: "#fff" }}
            data-testid="pi-date-from" />
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="px-3 py-2 text-sm rounded-md border outline-none" style={{ borderColor: "var(--border)", background: "#fff" }}
            data-testid="pi-date-to" />
          {hasFilters && (
            <button onClick={clearFilters} className="btn-secondary" data-testid="pi-clear-filters">
              <FilterX className="w-4 h-4" /> Cancella filtri
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="card-shell p-8 flex items-center justify-center text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Calcolo distanze e meteo…</div>
      ) : data.items.length === 0 ? (
        <div className="card-shell p-12 text-center">
          <Route className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--text-muted)" }} />
          <div className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="pi-empty">
            {error ? error : "Nessun intervento pianificato. Compila la sezione Pianificazione di un cliente."}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {data.items.map((it, idx) => (
            <div key={it.pianificazione_id} className="card-shell p-4 flex flex-col lg:flex-row gap-4" data-testid={`pi-item-${it.pianificazione_id}`}>
              {/* Index + distance */}
              <div className="flex lg:flex-col items-center gap-3 lg:w-24 lg:border-r" style={{ borderColor: "var(--border)" }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ background: "var(--primary)" }}>
                  {idx + 1}
                </div>
                {it.distanza_km != null ? (
                  <div className="text-center">
                    <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--text-muted)" }}>Distanza</div>
                    <div className="text-xl font-extrabold" style={{ color: "var(--accent)", fontFamily: "Manrope" }}>{it.distanza_km}<span className="text-xs ml-0.5">km</span></div>
                  </div>
                ) : (
                  <span className="text-xs" style={{ color: "#B30000" }}>Geo non risolta</span>
                )}
              </div>

              {/* Main content */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
                  <Link to={`/clienti/${it.cliente_id}`} className="font-bold text-lg hover:underline">
                    {it.cliente_nome}
                  </Link>
                  <span className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>
                    {fmtDate(it.data_inizio)} · {fmtTime(it.data_inizio)} · {it.durata_ore}h
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {it.squadra_name ? (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: it.squadra_color + "22", color: it.squadra_color }}>
                      {it.squadra_name}
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "#FDECEA", color: "#B30000" }}>
                      Nessuna squadra
                    </span>
                  )}
                  <span className="text-xs inline-flex items-center gap-1" style={{ color: "var(--text-muted)" }}>
                    <MapPin className="w-3 h-3" />
                    {[it.cliente_indirizzo, it.cliente_citta, it.cliente_provincia && `(${it.cliente_provincia})`].filter(Boolean).join(", ")}
                  </span>
                  {it.cliente_telefono && (
                    <span className="text-xs inline-flex items-center gap-1" style={{ color: "var(--text-muted)" }}>
                      <Phone className="w-3 h-3" />{it.cliente_telefono}
                    </span>
                  )}
                </div>

                {/* Meteo + costi inline */}
                <div className="flex flex-wrap gap-2 items-center">
                  <MeteoBadge meteo={it.meteo} />
                  {it.costi && (
                    <>
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full" style={{ background: "#FFF4E5", color: "#7A4500" }} data-testid={`pi-fuel-${it.pianificazione_id}`}>
                        <Fuel className="w-3 h-3" /> €{it.costi.costo_carburante.toFixed(2)} ({it.costi.litri_carburante}L)
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full" style={{ background: "#F3E8FF", color: "#6B21A8" }} data-testid={`pi-toll-${it.pianificazione_id}`}>
                        <ReceiptText className="w-3 h-3" /> caselli €{it.costi.costo_caselli.toFixed(2)}
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: "var(--primary)", color: "#fff" }} data-testid={`pi-total-${it.pianificazione_id}`}>
                        Tot. €{it.costi.totale.toFixed(2)} · {it.costi.distanza_totale_km}km a/r
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function KPI({ icon: Icon, label, value, testid }) {
  return (
    <div className="card-shell p-4 flex items-center gap-3" data-testid={testid}>
      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "var(--surface-muted)", color: "var(--primary)" }}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--text-muted)" }}>{label}</div>
        <div className="text-xl font-extrabold" style={{ fontFamily: "Manrope" }}>{value}</div>
      </div>
    </div>
  );
}
